import React, { useEffect, useState } from 'react'
import { X, Save } from 'lucide-react'
import { quickReplyService } from '../../services/quickReplyService'
import type { QuickReply } from '../../types/quickReply.types'
import toast from 'react-hot-toast'

export interface QuickReplyFormModalProps {
  isOpen: boolean
  onClose: () => void
  /** null = cadastrar nova mensagem; QuickReply = editar existente */
  quickReply: QuickReply | null
  onSaved: (quickReply: QuickReply) => void
}

/**
 * Modal reutilizável para cadastrar ou editar mensagem rápida.
 * Título e conteúdo obrigatórios.
 */
export const QuickReplyFormModal: React.FC<QuickReplyFormModalProps> = ({
  isOpen,
  onClose,
  quickReply,
  onSaved
}) => {
  const isEdit = quickReply != null
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (quickReply) {
      setTitle(quickReply.title)
      setContent(quickReply.content)
    } else {
      setTitle('')
      setContent('')
    }
  }, [quickReply, isOpen])

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Título e conteúdo são obrigatórios')
      return
    }
    try {
      setIsSaving(true)
      if (isEdit && quickReply) {
        const res = await quickReplyService.updateQuickReply(quickReply.id, {
          title: title.trim(),
          content: content.trim()
        })
        if (res.success && res.data) {
          onSaved(res.data)
          onClose()
          toast.success('Mensagem rápida atualizada')
        } else {
          toast.error(res.message || 'Erro ao atualizar mensagem rápida')
        }
      } else {
        const res = await quickReplyService.createQuickReply({
          title: title.trim(),
          content: content.trim()
        })
        if (res.success && res.data) {
          onSaved(res.data)
          onClose()
          toast.success('Mensagem rápida criada')
        } else {
          toast.error(res.message || 'Erro ao criar mensagem rápida')
        }
      }
    } catch {
      toast.error(isEdit ? 'Erro ao atualizar mensagem rápida' : 'Erro ao criar mensagem rápida')
    } finally {
      setIsSaving(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSaving) onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Editar mensagem rápida' : 'Nova mensagem rápida'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 disabled:opacity-50 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="qr-form-title" className="block text-sm font-medium text-gray-700 mb-1">
              Título
            </label>
            <input
              id="qr-form-title"
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Ex: Saudação padrão"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="qr-form-content" className="block text-sm font-medium text-gray-700 mb-1">
              Conteúdo
            </label>
            <textarea
              id="qr-form-content"
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Conteúdo da mensagem rápida..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-500 rounded-lg hover:bg-indigo-600 disabled:opacity-50 transition-colors"
          >
            {isSaving ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEdit ? 'Atualizar' : 'Salvar'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
